import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

// ====================================================================
// BanksPage                                            v22  Stage S1
// --------------------------------------------------------------------
// What changed:
//
//  1. BUG BK1 FIXED. The form never sent the pre-shipment and
//     post-shipment limits, but the server wrote them anyway - so both
//     figures were wiped blank every time any bank was edited. The two
//     fields are now on the form and are always sent.
//
//  2. EEFC LEDGERS. A bank can now hold EEFC accounts, one per
//     currency - USD, EUR, GBP, JPY, AUD, AED and so on side by side.
//     Each ledger shows its balance, the average cost rate of the money
//     still in it, and its movements and parcels.
//
// Money is only PUT IN here (as an opening balance). Converting it back
// to rupees happens on the Realisation page, and paying an import with
// it on the Import page - both in later stages.
//
// No profit or loss is ever shown against money sitting in an EEFC
// account. Nothing has been made or lost until it leaves.
// ====================================================================

const CURRENCIES = ['USDINR', 'EURINR', 'GBPINR', 'JPYINR', 'AUDINR', 'AEDINR', 'SGDINR'];

const pairLabel = (c) =>
  c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—');

const ccyCode = (c) => (c && c.length === 6 ? c.slice(0, 3) : (c || ''));

const fmt = (n, dp = 2) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const fmt0 = (n) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// Blank number box -> 0 (which clears the figure). A real 0 shows blank.
const numOrZero = (v) => (v === '' || v === null || v === undefined ? 0 : parseFloat(v));
const showNum   = (v) => (v === null || v === undefined || Number(v) === 0 ? '' : String(v));

const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title === 'string') return d.title;
  return fallback;
};

const TXN_LABEL = {
  CREDIT_OPENING:     'Opening balance',
  CREDIT_REALISATION: 'Parked from realisation',
  CREDIT_DISCOUNTING: 'Parked from discounting',
  CREDIT_OTHER:       'Other receipt',
  DEBIT_CONVERSION:   'Converted to INR',
  DEBIT_IMPORT:       'Import payment',
  DEBIT_PCFC:         'PCFC repayment',
  DEBIT_CHARGES:      'Bank charges',
  DEBIT_OTHER:        'Other payment'
};

const SOURCE_LABEL = {
  OPENING:     'Opening',
  REALISATION: 'Realisation',
  DISCOUNTING: 'Discounting',
  OTHER:       'Other'
};

const emptyBankForm = {
  bankName: '', branch: '', preshipmentLimit: '', postshipmentLimit: '', notes: ''
};

const emptyEefcForm = {
  currency: 'USDINR', accountNo: '',
  openingFc: '', openingRate: '', openingDate: '', notes: ''
};

function BanksPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [banks, setBanks]               = useState([]);
  const [eefc, setEefc]                 = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingBank, setEditingBank]   = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm]                 = useState(emptyBankForm);
  const [saving, setSaving]             = useState(false);

  // EEFC ledger form
  const [eefcForm, setEefcForm]         = useState(emptyEefcForm);
  const [eefcBank, setEefcBank]         = useState(null);   // bank the new ledger belongs to
  const [editingEefc, setEditingEefc]   = useState(null);
  const [showEefcForm, setShowEefcForm] = useState(false);

  // Expanded ledger detail
  const [openAccount, setOpenAccount]   = useState(null);
  const [detailTab, setDetailTab]       = useState('ledger');
  const [movements, setMovements]       = useState([]);
  const [lots, setLots]                 = useState([]);
  const [detailBusy, setDetailBusy]     = useState(false);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const loadAll = async () => {
    await Promise.all([loadBanks(), loadEefc()]);
  };

  const loadBanks = async () => {
    try {
      setLoading(true);
      const url = showInactive
        ? `${CONFIG.API}/api/bills/banks?status=ALL`
        : `${CONFIG.API}/api/bills/banks`;
      const res = await axios.get(url, auth);
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadEefc = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks/eefc?status=ALL`, auth);
      setEefc(res.data);
    } catch (err) {
      console.error(err);
      setEefc([]);
    }
  };

  // ---------------- bank form ----------------
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm(emptyBankForm);
    setEditingBank(null);
    setShowForm(false);
  };

  const handleEditClick = (bank) => {
    setForm({
      bankName:          bank.bankName || '',
      branch:            bank.branch   || '',
      preshipmentLimit:  showNum(bank.preshipmentLimit),
      postshipmentLimit: showNum(bank.postshipmentLimit),
      notes:             typeof bank.notes === 'string' ? bank.notes : ''
    });
    setEditingBank(bank);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!form.bankName.trim()) { alert('Please enter the bank name'); return; }

    // BK1: the limits are now always sent. Leaving a box empty sends 0,
    // which clears that limit; it no longer wipes it by accident.
    const payload = {
      bankName:          form.bankName.trim(),
      branch:            form.branch.trim(),
      preshipmentLimit:  numOrZero(form.preshipmentLimit),
      postshipmentLimit: numOrZero(form.postshipmentLimit),
      notes:             form.notes || null
    };

    try {
      setSaving(true);
      if (editingBank) {
        await axios.put(`${CONFIG.API}/api/bills/banks/${editingBank.bankId}`, payload, auth);
      } else {
        await axios.post(`${CONFIG.API}/api/bills/banks`, payload, auth);
      }
      resetForm();
      loadAll();
    } catch (err) {
      alert(serverMessage(err, 'Error saving bank. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (bank) => {
    if (!window.confirm(
      `Deactivate ${bank.bankName}? It will stop appearing in dropdowns, but old records keep it.`
    )) return;
    try {
      await axios.put(`${CONFIG.API}/api/bills/banks/${bank.bankId}/deactivate`, {}, auth);
      loadAll();
    } catch (err) {
      alert(serverMessage(err, 'Error deactivating bank.'));
    }
  };

  const handleReactivate = async (bank) => {
    try {
      await axios.put(`${CONFIG.API}/api/bills/banks/${bank.bankId}/reactivate`, {}, auth);
      loadAll();
    } catch (err) {
      alert(serverMessage(err, 'Error reactivating bank.'));
    }
  };

  // ---------------- EEFC ledgers ----------------
  const openNewEefc = (bank) => {
    setEefcForm(emptyEefcForm);
    setEefcBank(bank);
    setEditingEefc(null);
    setShowEefcForm(true);
    window.scrollTo(0, 0);
  };

  const openEditEefc = (acct) => {
    setEefcForm({
      currency:    acct.currency || 'USDINR',
      accountNo:   acct.accountNo || '',
      openingFc:   showNum(acct.openingFc),
      openingRate: showNum(acct.openingRate),
      openingDate: (acct.openingDate || '').substring(0, 10),
      notes:       acct.notes || ''
    });
    setEefcBank({ bankId: acct.bankId, bankName: acct.bankName });
    setEditingEefc(acct);
    setShowEefcForm(true);
    window.scrollTo(0, 0);
  };

  const resetEefcForm = () => {
    setEefcForm(emptyEefcForm);
    setEefcBank(null);
    setEditingEefc(null);
    setShowEefcForm(false);
  };

  const handleEefcChange = (e) =>
    setEefcForm({ ...eefcForm, [e.target.name]: e.target.value });

  const handleEefcSubmit = async () => {
    const openingFc = numOrZero(eefcForm.openingFc);
    if (openingFc < 0) { alert('Opening balance cannot be negative'); return; }
    if (openingFc > 0 && numOrZero(eefcForm.openingRate) <= 0) {
      alert('An opening balance needs its book rate, so profit on it can be worked out later.');
      return;
    }
    if (openingFc > 0 && !eefcForm.openingDate) {
      alert('An opening balance needs the date it applies from.');
      return;
    }

    const payload = {
      currency:    eefcForm.currency,
      accountNo:   eefcForm.accountNo || null,
      openingFc:   openingFc,
      openingRate: numOrZero(eefcForm.openingRate),
      openingDate: eefcForm.openingDate || null,
      notes:       eefcForm.notes || null
    };

    try {
      setSaving(true);
      if (editingEefc) {
        await axios.put(
          `${CONFIG.API}/api/bills/banks/eefc/${editingEefc.accountId}`, payload, auth);
      } else {
        await axios.post(
          `${CONFIG.API}/api/bills/banks/${eefcBank.bankId}/eefc`, payload, auth);
      }
      resetEefcForm();
      loadEefc();
      if (openAccount) refreshDetail(openAccount, detailTab);
    } catch (err) {
      alert(serverMessage(err, 'Error saving the EEFC ledger.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEefc = async (acct) => {
    if (!window.confirm(
      `Close the ${pairLabel(acct.currency)} ledger at ${acct.bankName}?\n\n` +
      'It will stop being offered for new entries. Its history stays.'
    )) return;
    try {
      await axios.put(`${CONFIG.API}/api/bills/banks/eefc/${acct.accountId}/close`, {}, auth);
      loadEefc();
    } catch (err) {
      alert(serverMessage(err, 'Could not close this ledger.'));
    }
  };

  const handleReopenEefc = async (acct) => {
    try {
      await axios.put(`${CONFIG.API}/api/bills/banks/eefc/${acct.accountId}/reopen`, {}, auth);
      loadEefc();
    } catch (err) {
      alert(serverMessage(err, 'Could not reopen this ledger.'));
    }
  };

  const refreshDetail = async (accountId, tab) => {
    try {
      setDetailBusy(true);
      if (tab === 'lots') {
        const res = await axios.get(
          `${CONFIG.API}/api/bills/banks/eefc/${accountId}/lots`, auth);
        setLots(res.data);
      } else {
        const res = await axios.get(
          `${CONFIG.API}/api/bills/banks/eefc/${accountId}/ledger`, auth);
        setMovements(res.data.movements || []);
      }
    } catch (err) {
      console.error(err);
      if (tab === 'lots') setLots([]); else setMovements([]);
    } finally {
      setDetailBusy(false);
    }
  };

  const toggleDetail = async (acct, tab) => {
    if (openAccount === acct.accountId && detailTab === tab) {
      setOpenAccount(null);
      return;
    }
    setOpenAccount(acct.accountId);
    setDetailTab(tab);
    await refreshDetail(acct.accountId, tab);
  };

  // ---------------- derived ----------------
  const eefcFor = (bankId) => eefc.filter(a => a.bankId === bankId);

  // Total held per currency, across every bank
  const byCurrency = {};
  eefc.forEach(a => {
    if (Number(a.balanceFc) > 0.005) {
      byCurrency[a.currency] = (byCurrency[a.currency] || 0) + Number(a.balanceFc);
    }
  });
  const currencyTotals = Object.keys(byCurrency).sort();

  const activeBanks = banks.filter(b => b.status === 'ACTIVE');

  const cell = { padding: '7px 10px' };
  const rightCell = { ...cell, textAlign: 'right' };
  const muted = { fontSize: '12px', color: '#64748B' };

  return (
    <div className="content">

      {/* Summary cards */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Active banks</div>
          <div className="metric-value val-green">{activeBanks.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Total banks</div>
          <div className="metric-value val-blue">{banks.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">EEFC ledgers</div>
          <div className="metric-value val-blue">
            {eefc.filter(a => a.status === 'ACTIVE').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Currencies held</div>
          <div className="metric-value val-amber">{currencyTotals.length}</div>
        </div>
      </div>

      {/* EEFC held, per currency */}
      {currencyTotals.length > 0 && (
        <div style={{
          background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px',
          display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#0C4A6E' }}>
            EEFC held
          </span>
          {currencyTotals.map(c => (
            <span key={c} style={{ fontSize: '13px' }}>
              <strong>{pairLabel(c)}</strong>{' '}
              {fmt(byCurrency[c])} {ccyCode(c)}
            </span>
          ))}
        </div>
      )}

      {/* ============================================================ */}
      {/* BANKS                                                        */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">Manage banks</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setForm(emptyBankForm); setEditingBank(null); setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New bank'}
        </button>
      </div>

      <div style={{ ...muted, marginBottom: '14px' }}>
        Banks added here appear in the bank dropdown on every page (PCFC, discounting,
        realisation, forwards, import). One spelling per bank keeps records matched.
      </div>

      {/* New / Edit bank form */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '640px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingBank ? '✏️ Edit bank' : '🏦 New bank'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Bank name</label>
              <input name="bankName" value={form.bankName}
                onChange={handleChange} placeholder="e.g. SBIN" />
            </div>
            <div className="form-field">
              <label>Branch (optional)</label>
              <input name="branch" value={form.branch}
                onChange={handleChange} placeholder="e.g. Kochi" />
            </div>
          </div>

          {/* These two were missing from the form, which is why editing a
              bank used to wipe them. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '4px' }}>
            <div className="form-field">
              <label>Pre-shipment limit (INR, optional)</label>
              <input type="number" name="preshipmentLimit" value={form.preshipmentLimit}
                onChange={handleChange} placeholder="e.g. 5000000" />
            </div>
            <div className="form-field">
              <label>Post-shipment limit (INR, optional)</label>
              <input type="number" name="postshipmentLimit" value={form.postshipmentLimit}
                onChange={handleChange} placeholder="e.g. 7000000" />
            </div>
          </div>
          <div style={{ ...muted, marginBottom: '14px' }}>
            Leave a limit empty if the bank has not set one. Clearing a box removes that limit.
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Any additional notes..."
                rows={2}
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : (editingBank ? '✓ Update bank' : '✓ Save bank')}
            </button>
          </div>
        </div>
      )}

      {/* Show inactive toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <label style={{ fontSize: '13px', color: '#64748B', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Show inactive banks
        </label>
      </div>

      {/* Banks table */}
      <div className="table-wrap" style={{ marginBottom: '26px' }}>
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : banks.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No banks yet. Click "+ New bank" to add your first one.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Bank name</th>
                <th>Branch</th>
                <th style={{ textAlign: 'right' }}>Pre-shipment</th>
                <th style={{ textAlign: 'right' }}>Post-shipment</th>
                <th>EEFC</th>
                <th>Notes</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {banks.map(b => {
                const mine = eefcFor(b.bankId).filter(a => a.status === 'ACTIVE');
                return (
                  <tr key={b.bankId}>
                    <td>{b.bankName}</td>
                    <td>{b.branch || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmt0(b.preshipmentLimit)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt0(b.postshipmentLimit)}</td>
                    <td>
                      {mine.length === 0
                        ? <span style={{ color: '#94A3B8' }}>—</span>
                        : mine.map(a => ccyCode(a.currency)).join(', ')}
                    </td>
                    <td>{b.notes || '—'}</td>
                    <td>
                      <span className={`badge ${b.status === 'ACTIVE' ? 'badge-open' : 'badge-cancelled'}`}>
                        {b.status}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-secondary"
                        onClick={() => handleEditClick(b)}
                        style={{ marginRight: '6px' }}>
                        Edit
                      </button>
                      {b.status === 'ACTIVE' ? (
                        <button className="btn-danger" onClick={() => handleDeactivate(b)}>
                          Deactivate
                        </button>
                      ) : (
                        <button className="btn-secondary" onClick={() => handleReactivate(b)}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ============================================================ */}
      {/* EEFC LEDGERS                                                 */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">EEFC ledgers</div>
      </div>

      <div style={{ ...muted, marginBottom: '14px' }}>
        One ledger per bank per currency. Money is parked here from the Discounting and
        Realisation pages; it is converted back to rupees on the Realisation page, or used
        to pay an import on the Import page. Profit or loss is worked out only when money
        leaves a ledger — never while it is sitting here.
      </div>

      {/* New / Edit ledger form */}
      {showEefcForm && (
        <div className="form-card" style={{ maxWidth: '760px' }}>
          <div className="form-step" style={{ marginBottom: '6px' }}>
            {editingEefc ? '✏️ Edit EEFC ledger' : '💱 New EEFC ledger'}
          </div>
          <div style={{ ...muted, marginBottom: '16px' }}>
            Bank: <strong>{eefcBank?.bankName}</strong>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Currency</label>
              <select name="currency" value={eefcForm.currency} onChange={handleEefcChange}>
                {CURRENCIES.map(c => (
                  <option key={c} value={c}>{pairLabel(c)}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Account number (optional)</label>
              <input name="accountNo" value={eefcForm.accountNo}
                onChange={handleEefcChange} placeholder="Bank account reference" />
            </div>
          </div>

          <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151',
                        marginTop: '6px', marginBottom: '8px' }}>
            Opening balance (only if this ledger already holds money today)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '4px' }}>
            <div className="form-field">
              <label>Opening FC amount</label>
              <input type="number" name="openingFc" value={eefcForm.openingFc}
                onChange={handleEefcChange} placeholder="0" />
            </div>
            <div className="form-field">
              <label>Book rate of that balance</label>
              <input type="number" name="openingRate" value={eefcForm.openingRate}
                onChange={handleEefcChange} placeholder="e.g. 83.50" />
            </div>
            <div className="form-field">
              <label>Applies from</label>
              <input type="date" name="openingDate" value={eefcForm.openingDate}
                onChange={handleEefcChange} />
            </div>
          </div>
          <div style={{ ...muted, marginBottom: '14px' }}>
            The book rate is the yardstick for profit later. Everything earned above it from
            today onwards counts as gain from holding the money.
          </div>

          {editingEefc && (
            <div style={{
              background: '#FEF9C3', border: '1px solid #FDE68A', borderRadius: '6px',
              padding: '10px 14px', fontSize: '13px', color: '#92400E', marginBottom: '14px'
            }}>
              The currency and opening balance can only be changed while nothing has used this
              ledger. Once money has been parked in or taken out, only the account number and
              notes can be edited.
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea name="notes" value={eefcForm.notes} onChange={handleEefcChange}
                rows={2} placeholder="Any additional notes..."
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetEefcForm}>Cancel</button>
            <button className="btn-primary" onClick={handleEefcSubmit} disabled={saving}>
              {saving ? 'Saving...' : (editingEefc ? '✓ Update ledger' : '✓ Create ledger')}
            </button>
          </div>
        </div>
      )}

      {/* One block per bank */}
      {activeBanks.length === 0 ? (
        <p style={{ padding: '14px', color: '#64748B' }}>
          Add a bank first — EEFC ledgers sit underneath a bank.
        </p>
      ) : activeBanks.map(bank => {
        const mine = eefcFor(bank.bankId);
        return (
          <div key={bank.bankId} style={{
            border: '1px solid #E2E8F0', borderRadius: '8px',
            marginBottom: '14px', overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: '#F8FAFC', padding: '10px 14px', borderBottom: '1px solid #E2E8F0'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1E293B' }}>
                🏦 {bank.bankName}
                {bank.branch ? <span style={{ ...muted, fontWeight: '400' }}> — {bank.branch}</span> : null}
              </div>
              <button className="btn-secondary" onClick={() => openNewEefc(bank)}>
                + EEFC ledger
              </button>
            </div>

            {mine.length === 0 ? (
              <div style={{ padding: '12px 14px', ...muted }}>
                No EEFC ledgers for this bank yet.
              </div>
            ) : (
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {mine.map(a => (
                    <div key={a.accountId} style={{
                      border: '1px solid #E2E8F0', borderRadius: '8px',
                      padding: '12px 14px', minWidth: '260px',
                      background: a.status === 'ACTIVE' ? '#FFFFFF' : '#F8FAFC',
                      opacity: a.status === 'ACTIVE' ? 1 : 0.7
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#1F4E79' }}>
                          {pairLabel(a.currency)}
                        </span>
                        <span className={`badge ${a.status === 'ACTIVE' ? 'badge-open' : 'badge-cancelled'}`}>
                          {a.status}
                        </span>
                      </div>

                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                        {fmt(a.balanceFc)} <span style={{ fontSize: '13px' }}>{ccyCode(a.currency)}</span>
                      </div>
                      <div style={{ ...muted, marginBottom: '8px' }}>
                        Average cost {a.avgCostRate ? Number(a.avgCostRate).toFixed(4) : '—'}
                        {a.openLots ? ` · ${a.openLots} parcel${a.openLots > 1 ? 's' : ''}` : ''}
                        {a.oldestLotDate ? ` · oldest ${String(a.oldestLotDate).substring(0, 10)}` : ''}
                      </div>
                      {a.accountNo && (
                        <div style={{ ...muted, marginBottom: '8px' }}>A/c {a.accountNo}</div>
                      )}

                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button className="btn-secondary"
                          onClick={() => toggleDetail(a, 'ledger')}>
                          {openAccount === a.accountId && detailTab === 'ledger' ? '▲ Ledger' : '▼ Ledger'}
                        </button>
                        <button className="btn-secondary"
                          onClick={() => toggleDetail(a, 'lots')}>
                          {openAccount === a.accountId && detailTab === 'lots' ? '▲ Parcels' : '▼ Parcels'}
                        </button>
                        <button className="btn-secondary" onClick={() => openEditEefc(a)}>Edit</button>
                        {a.status === 'ACTIVE' ? (
                          <button className="btn-danger" onClick={() => handleCloseEefc(a)}>Close</button>
                        ) : (
                          <button className="btn-secondary" onClick={() => handleReopenEefc(a)}>Reopen</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detail panel for the expanded ledger of this bank */}
                {mine.some(a => a.accountId === openAccount) && (
                  <div style={{
                    marginTop: '14px', border: '1px solid #BAE6FD',
                    borderRadius: '8px', background: '#F0F9FF', padding: '12px 14px'
                  }}>
                    {detailBusy ? (
                      <div style={muted}>Loading...</div>
                    ) : detailTab === 'ledger' ? (
                      movements.length === 0 ? (
                        <div style={muted}>
                          Nothing has moved through this ledger yet.
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>What happened</th>
                                <th style={{ textAlign: 'right' }}>In</th>
                                <th style={{ textAlign: 'right' }}>Out</th>
                                <th style={{ textAlign: 'right' }}>Balance</th>
                                <th style={{ textAlign: 'right' }}>Rate</th>
                                <th style={{ textAlign: 'right' }}>Gain (INR)</th>
                                <th>Reference</th>
                              </tr>
                            </thead>
                            <tbody>
                              {movements.map(m => (
                                <tr key={m.txnId}>
                                  <td style={cell}>{String(m.txnDate || '').substring(0, 10)}</td>
                                  <td style={cell}>{TXN_LABEL[m.txnType] || m.txnType}</td>
                                  <td style={rightCell}>{m.fcIn > 0 ? fmt(m.fcIn) : '—'}</td>
                                  <td style={rightCell}>{m.fcOut > 0 ? fmt(m.fcOut) : '—'}</td>
                                  <td style={{ ...rightCell, fontWeight: '600' }}>{fmt(m.balanceAfter)}</td>
                                  <td style={rightCell}>
                                    {m.exitRate ? Number(m.exitRate).toFixed(4) : '—'}
                                  </td>
                                  <td style={{
                                    ...rightCell,
                                    color: Number(m.gainInr) > 0 ? '#166534'
                                         : Number(m.gainInr) < 0 ? '#B91C1C' : '#64748B',
                                    fontWeight: Number(m.gainInr) ? '600' : '400'
                                  }}>
                                    {Number(m.gainInr) ? fmt0(m.gainInr) : '—'}
                                  </td>
                                  <td style={cell}>{m.reference || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ ...muted, marginTop: '8px' }}>
                            Money coming in never shows a gain. Profit or loss appears only on the
                            row where money leaves.
                          </div>
                        </div>
                      )
                    ) : (
                      lots.length === 0 ? (
                        <div style={muted}>No parcels in this ledger yet.</div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Came from</th>
                                <th>Invoice</th>
                                <th style={{ textAlign: 'right' }}>Amount</th>
                                <th style={{ textAlign: 'right' }}>Used</th>
                                <th style={{ textAlign: 'right' }}>Remaining</th>
                                <th style={{ textAlign: 'right' }}>Cost rate</th>
                                <th style={{ textAlign: 'right' }}>Rate on arrival</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lots.map(l => (
                                <tr key={l.lotId}
                                  style={{ opacity: Number(l.fcRemaining) > 0.005 ? 1 : 0.55 }}>
                                  <td style={cell}>{String(l.lotDate || '').substring(0, 10)}</td>
                                  <td style={cell}>{SOURCE_LABEL[l.sourceType] || l.sourceType}</td>
                                  <td style={cell}>{l.invoiceNo || '—'}</td>
                                  <td style={rightCell}>{fmt(l.fcAmount)}</td>
                                  <td style={rightCell}>{fmt(l.fcUsed)}</td>
                                  <td style={{ ...rightCell, fontWeight: '600' }}>{fmt(l.fcRemaining)}</td>
                                  <td style={rightCell}>{Number(l.costRate).toFixed(4)}</td>
                                  <td style={rightCell}>{Number(l.benchmarkRate).toFixed(4)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ ...muted, marginTop: '8px' }}>
                            Oldest first — this is the order money will leave in. Faded rows are
                            parcels that have been fully used.
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
}

export default BanksPage;